import type { permissionList } from "@/@layouts/types";
import navItems from "@/navigation/vertical";
import { pb, RolesRecord } from "@/utils/PocketBaseAdapter";
import { usePermission } from "@/utils/usePermission";
import isEmpty from "lodash/isEmpty";

export const useUserRole = () => {
  // Interface
  interface permissionMap {
    [key: string]: permissionList;
  }

  // composable
  const usePermissions = usePermission();
  const { isHidden, meetsMinimum } = usePermissions;

  // Data
  const rolesList = ref<any[]>([]);
  const userInfo = ref<any>({});
  const permissionPriority = ["Create", "Edit", "Assign", "View", "None"];
  const permission = ref<any>();
  const refetchPermission = ref<number>(1);

  function safelyParseJSON(json) {
    // This function cannot be optimised, it's best to
    // keep it small!
    var parsed;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      // Oh well, but whatever...
    }
    return parsed; // Could be undefined!
  }

  const isUserMode = ref<any>(
    safelyParseJSON(sessionStorage.getItem("userMode")) || "false"
  );

  const permissionListMap = ref<permissionMap>({});

  // Computed
  const calculatePermissions = async () => {
    const { useUserStore } = await import("@/utils/UserStore");
    const currentLocation = useUserStore().currentLocation;
    const relevantRoles = rolesList.value?.filter(
      (role) =>
        userInfo.value?.roles?.includes(role.id) &&
        role.location === currentLocation
    );
    const aggregatedPermissions = relevantRoles?.reduce((acc, role) => {
      Object.keys(role).forEach((permissionKey) => {
        if (!acc[permissionKey]) {
          acc[permissionKey] = new Set();
        }
        acc[permissionKey].add(role[permissionKey]);
      });
      return acc;
    }, {});

    Object.keys(aggregatedPermissions)?.forEach((permissionKey) => {
      const permissionsMap = Array.from(aggregatedPermissions[permissionKey]);
      if (permissionsMap.some((el: any) => permissionPriority?.includes(el))) {
        aggregatedPermissions[permissionKey] =
          permissionPriority?.find((permission) =>
            permissionsMap?.includes(permission)
          ) || "None";
      } else {
        aggregatedPermissions[permissionKey] = permissionsMap;
      }
    });

    return await aggregatedPermissions;
  };

  /**
   * Computed property to generate the list of navigation items based on user permissions.
   *
   * @returns {Array} - An array of navigation items with their 'disable' property set based on user permissions.
   */
  const navListItems = computed(() => {
    return navItems.reduce((acc: any, el: any) => {
      // Skip nav items where the role doesn't meet the required permission tier.
      if (isHidden(el, permission.value)) return acc;

      // If the navigation item does not require a permission or the user has the required permission,
      // add it to the list.
      if (
        !el.permissionRequired ||
        (permission.value &&
          permission.value[el.permissionRequired] &&
          permission.value[el.permissionRequired].includes(true))
      ) {
        acc.push({ ...el, disable: el.disable || false });
      }

      return acc;
    }, []);
  });

  const getUserRolePermission = async () => {
    try {
      if (!rolesList.value.length) {
        const recordList = await pb
          .collection("roles")
          .getFullList<RolesRecord>();

        rolesList.value = recordList;
        refetchPermission.value = Math.random();
      }

      if (
        !isEmpty(userInfo.value) &&
        pb?.authStore?.model?.id === userInfo?.value?.id
      ) {
        return;
      } else {
        const record = await pb
          .collection("users")
          .getOne(pb?.authStore?.model?.id, { requestKey: "userprofile" });
        userInfo.value = record;
      }
    } catch (e) {}
  };

  const havePermission = async (requestedPath: string) => {
    if (isEmpty(permission.value)) {
      permission.value = await calculatePermissions();
    }

    // Mapping of paths to permission keys and required conditions
    const permissionMap: Record<string, { key: string; minPermission?: string }> =
      {
        // POS requires Edit on dockets, matching the nav item's threshold
        pos: { key: "dockets", minPermission: "Edit" },
        "pos-checkout": { key: "dockets", minPermission: "Edit" },
        dockets: { key: "dockets" },
        products: { key: "products" },
        user: { key: "users" },
        staffing: { key: "staffing" },
        bookinglist: { key: "bookings" },
        events: { key: "events" },
        areastatus: { key: "areaStatus" },
        matrixdisplay: { key: "matrixDisplays" },
        giftvouchers: { key: "giftVouchers" },
        "discount-codes": { key: "discountCodes" },
        transactions: { key: "posCash" },
        reports: { key: "reporting" },
        accesscontrol: { key: "doors" },
        "faqs-admin": { key: "faqs" },
        "faqs-admin-list": { key: "faqs" },
        location: { key: "locations" },
        activities: { key: "activities" },
        areas: { key: "areas" },
        advertising: { key: "advertising" },
        memberships: { key: "memberships" },
        coupontypes: { key: "coupons" },
        roles: { key: "roles" },
        emailtemplates: { key: "emailTemplates" },
        settings: { key: "settings" },
        "staff-timeclock": { key: "staffing" },
      };

    // Get permissions for the requested path
    const permissionData = permissionMap[requestedPath];

    if (permissionData) {
      const { key, minPermission } = permissionData;
      return minPermission
        ? meetsMinimum(permission.value[key], minPermission)
        : permission.value[key] && permission.value?.[key] !== "None";
    }

    // Default case
    return true;
  };

  const clearPermissionStore = () => {
    permission.value = {};
    permissionListMap.value = {};
    rolesList.value = [];
    userInfo.value = null;
  };

  watch(
    () => refetchPermission.value,
    () => {
      permission.value = calculatePermissions();
    },
    { immediate: true, deep: true }
  );

  return {
    permission,
    permissionListMap,
    navListItems,
    rolesList,
    isUserMode,
    getUserRolePermission,
    havePermission,
    clearPermissionStore,
  };
};
